/**
 * Work blocks data: the `work_blocks` collection, its one-row
 * `work_block_settings`, and the two Worker calls ("is it set up?" and "run
 * now"). The rules live in lib/workBlocks.ts.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { appMode, pb } from "@/lib/pocketbase";
import {
  resolveSettings, type BlockSettings, type Placement, type WorkBlock,
} from "@/lib/workBlocks";

const userId = (): string => (pb.authStore.model as { id?: string } | null)?.id ?? "";

export interface LastRun {
  at: string;
  weekStart: string;
  /** "auto": the run when you opened the app after your planning time. */
  source: "auto" | "button";
  placed: number;
  missed: number;
  kept: number;
  removed: number;
  errors: string[];
  /** Weeks already planned, newest first. */
  weeks?: string[];
}

interface SettingsRow {
  id: string;
  settings?: Partial<BlockSettings> | null;
  lastRun?: LastRun | null;
}

/** PocketBase sends "" and null for unset fields; the rules want absent. */
function clean(r: WorkBlock): WorkBlock {
  return {
    ...r,
    days: Array.isArray(r.days) ? r.days : [],
    itemIds: Array.isArray(r.itemIds) ? r.itemIds : [],
    lastSlots: Array.isArray(r.lastSlots) ? r.lastSlots : [],
    project: r.project || undefined,
    week: r.week || "",
    finishBy: r.finishBy || "",
    preferredDay: r.preferredDay || "",
  };
}

export function useWorkBlocks() {
  return useQuery({
    queryKey: ["work_blocks"],
    queryFn: async () =>
      (await pb.collection("work_blocks").getFullList<WorkBlock>({ sort: "position,created" })).map(clean),
  });
}

export function useWorkBlockSettings() {
  return useQuery({
    queryKey: ["work_block_settings"],
    queryFn: async () => {
      const row = (await pb.collection("work_block_settings").getFullList<SettingsRow>({ perPage: 1 }))[0];
      return {
        id: row?.id ?? "",
        settings: resolveSettings(row?.settings ?? null),
        lastRun: row?.lastRun && typeof row.lastRun === "object" && row.lastRun.at ? row.lastRun : null,
      };
    },
  });
}

export function useWorkBlockMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["work_blocks"] });

  const saveBlock = useMutation({
    mutationFn: (block: Partial<WorkBlock> & { id?: string }) => {
      const { id, lastSlots: _s, lastStatus: _t, lastRunAt: _r, ...data } = block;
      // An unset relation must be "" for PocketBase, not undefined (dropped).
      const body = { ...data, project: data.project ?? "", preferredDay: data.preferredDay ?? "" };
      return id
        ? pb.collection("work_blocks").update<WorkBlock>(id, body)
        : pb.collection("work_blocks").create<WorkBlock>({ ...body, user: userId() });
    },
    onSuccess: invalidate,
  });

  const deleteBlock = useMutation({
    mutationFn: (id: string) => pb.collection("work_blocks").delete(id),
    onSuccess: invalidate,
  });

  /** Store a manual order: writes only the blocks whose position changed. */
  const reorder = useMutation({
    mutationFn: async (ordered: WorkBlock[]) => {
      for (const [i, b] of ordered.entries()) {
        if (b.position !== i) await pb.collection("work_blocks").update(b.id, { position: i });
      }
    },
    onMutate: (ordered) => {
      qc.setQueryData<WorkBlock[]>(["work_blocks"], (all = []) => {
        const pos = new Map(ordered.map((b, i) => [b.id, i]));
        return all.map((b) => (pos.has(b.id) ? { ...b, position: pos.get(b.id) } : b));
      });
    },
    onSettled: invalidate,
  });

  /**
   * Make a week's one-off blocks match the plan: one per project with extra
   * time, updated in place where it already exists, removed where it no longer
   * does. Standing blocks are never touched.
   */
  const syncWeek = useMutation({
    mutationFn: async ({ week, wanted, existing }: {
      week: string;
      wanted: Omit<WorkBlock, "id">[];
      existing: WorkBlock[];
    }) => {
      const mine = existing.filter((b) => b.week === week);
      const byProject = new Map(mine.map((b) => [b.project ?? "", b]));
      for (const w of wanted) {
        const have = byProject.get(w.project ?? "");
        const body = { ...w, week, project: w.project ?? "", enabled: true };
        if (have) {
          await pb.collection("work_blocks").update(have.id, body);
          byProject.delete(w.project ?? "");
        } else {
          await pb.collection("work_blocks").create({ ...body, user: userId() });
        }
      }
      for (const stale of byProject.values()) await pb.collection("work_blocks").delete(stale.id);
    },
    onSettled: invalidate,
  });

  /** Creates the settings row on first save; there is only ever one. */
  const saveSettings = useMutation({
    mutationFn: async (settings: BlockSettings) => {
      const rows = await pb.collection("work_block_settings").getFullList<SettingsRow>({ perPage: 1 });
      return rows[0]
        ? pb.collection("work_block_settings").update(rows[0].id, { settings })
        : pb.collection("work_block_settings").create({ settings, user: userId() });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["work_block_settings"] }),
  });

  return { saveBlock, deleteBlock, reorder, syncWeek, saveSettings };
}

/* -- The Worker ------------------------------------------------------------- */

export interface WorkerConfig {
  token: boolean;
  scriptUrl: boolean;
  /** Why the check itself failed, if it did. */
  error?: string;
}

/** Whether the Worker has the secrets the script and the button need. */
export function useWorkBlockConfig() {
  return useQuery({
    queryKey: ["workblocks-config"],
    enabled: appMode === "live",
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<WorkerConfig> => {
      const res = await fetch("/workblocks/config", { headers: { Authorization: pb.authStore.token } });
      const body = (await res.json().catch(() => ({}))) as Partial<WorkerConfig> & { error?: string };
      if (!res.ok) return { token: false, scriptUrl: false, error: body.error ?? `HTTP ${res.status}` };
      return { token: !!body.token, scriptUrl: !!body.scriptUrl };
    },
  });
}

/** What the Apps Script answers with, for a preview or a real run. */
export interface RunResult {
  ok: boolean;
  dryRun: boolean;
  weekStart: string;
  placements: (Placement & { projectName?: string })[];
  results: { blockId: string; title: string; status: string; missed: number }[];
  counts: { placed: number; missed: number; kept: number; removed: number };
  freeMinutes: number;
  errors: string[];
  error?: string;
}

export function useRunBlocks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ weekStart, dryRun, source = "button" }: {
      weekStart: string; dryRun: boolean; source?: "auto" | "button";
    }): Promise<RunResult> => {
      const res = await fetch("/workblocks/run", {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: pb.authStore.token },
        body: JSON.stringify({ weekStart, dryRun, source }),
      });
      const body = (await res.json().catch(() => ({}))) as Partial<RunResult>;
      if (!res.ok || body.ok === false || body.error) throw new Error(body.error ?? `The run failed (HTTP ${res.status}).`);
      return {
        ok: true, dryRun, weekStart: body.weekStart ?? "",
        placements: body.placements ?? [], results: body.results ?? [],
        counts: { placed: 0, missed: 0, kept: 0, removed: 0, ...body.counts },
        freeMinutes: Number(body.freeMinutes) || 0, errors: body.errors ?? [],
      };
    },
    onSuccess: (r) => {
      if (r.dryRun) return;
      void qc.invalidateQueries({ queryKey: ["work_blocks"] });
      void qc.invalidateQueries({ queryKey: ["work_block_settings"] });
    },
  });
}
